import { CountryCodeEnum } from "@/lib/enum/country-code.enum";
import { ThgVehicleTypes } from "@/store/enum/thg/thg-vehicle-types.enum";

export interface IVehicleClassConfig {
  countryCode: CountryCodeEnum;
  vehicleType: ThgVehicleTypes;
  /** kWh allowance per vehicle for the THG quota in the given country. */
  amountInKwh: number;
  /** i18n token for the human-readable class name. */
  title: string;
  /** i18n token for the class-speaking long-form name. */
  classSpeaking: string;
  /** i18n token for the descriptive hint shown next to the class. */
  hint: string;
  /** Image used in the report-side vehicle-class listings. */
  src: string;
  /** Whether the class is featured as a best-seller in the report UI. */
  isBestSeller: boolean;
}

/**
 * Single source of truth for everything tied to a (countryCode, vehicleType)
 * pair: the kWh allowance plus the UI metadata used by the report-side
 * `ActivePrice` listing.
 *
 * Keeping kWh and UI metadata in the same row means TypeScript enforces
 * that every kWh entry has UI metadata (and vice versa), so adding a new
 * vehicle class can't silently drop out of either consumer.
 *
 * Consumed by:
 * - `ThgPriceHelper` — uses `countryCode`, `vehicleType`, `amountInKwh`
 * - `getKwhForVehicleClass` (commission helpers) — same three fields
 * - `ActivePrice` — uses the full row including UI metadata
 *
 * TODO: source kWh values from the backend so that adjustments don't
 * require a frontend redeploy.
 */
export const VEHICLE_CLASS_CONFIG: ReadonlyArray<IVehicleClassConfig> = [
  {
    countryCode: CountryCodeEnum.germany,
    vehicleType: ThgVehicleTypes.M1,
    amountInKwh: 2000,
    title: "report.thgVehicleClass.classes.de.M1.title",
    classSpeaking: "report.thgVehicleClass.classes.de.M1.classSpeaking",
    hint: "report.thgVehicleClass.classes.de.M1.hint",
    src: "https://static.mmmint.ai/public-thg/2023-09-14-logo-f3788982c40337cc4a923577.jpg",
    isBestSeller: true
  },
  {
    countryCode: CountryCodeEnum.germany,
    vehicleType: ThgVehicleTypes.N1,
    amountInKwh: 3000,
    title: "report.thgVehicleClass.classes.de.N1.title",
    classSpeaking: "report.thgVehicleClass.classes.de.N1.classSpeaking",
    hint: "report.thgVehicleClass.classes.de.N1.hint",
    src: "https://static.mmmint.ai/public-thg/2023-09-14-logo-074955d712439753ab46a14d.jpg",
    isBestSeller: true
  },
  {
    countryCode: CountryCodeEnum.germany,
    vehicleType: ThgVehicleTypes.other,
    amountInKwh: 2000,
    title: "report.thgVehicleClass.classes.de.other.title",
    classSpeaking: "report.thgVehicleClass.classes.de.other.classSpeaking",
    hint: "report.thgVehicleClass.classes.de.other.hint",
    src: "https://static.mmmint.ai/public-thg/2023-09-14-logo-e6d545957c8e9e278d8ab9eb.jpg",
    isBestSeller: true
  },
  {
    countryCode: CountryCodeEnum.germany,
    vehicleType: ThgVehicleTypes.N2,
    amountInKwh: 20600,
    title: "report.thgVehicleClass.classes.de.N2.title",
    classSpeaking: "report.thgVehicleClass.classes.de.N2.classSpeaking",
    hint: "report.thgVehicleClass.classes.de.N2.hint",
    src: "https://static.mmmint.ai/public-thg/2023-09-14-logo-7d7d49db7b71cda1662f89e4.jpg",
    isBestSeller: false
  },
  {
    countryCode: CountryCodeEnum.germany,
    vehicleType: ThgVehicleTypes.N3,
    amountInKwh: 33400,
    title: "report.thgVehicleClass.classes.de.N3.title",
    classSpeaking: "report.thgVehicleClass.classes.de.N3.classSpeaking",
    hint: "report.thgVehicleClass.classes.de.N3.hint",
    src: "https://static.mmmint.ai/public-thg/2023-09-14-logo-7d7d49db7b71cda1662f89e4.jpg",
    isBestSeller: false
  },
  {
    countryCode: CountryCodeEnum.germany,
    vehicleType: ThgVehicleTypes.M3,
    amountInKwh: 72000,
    title: "report.thgVehicleClass.classes.de.M3.title",
    classSpeaking: "report.thgVehicleClass.classes.de.M3.classSpeaking",
    hint: "report.thgVehicleClass.classes.de.M3.hint",
    src: "https://static.mmmint.ai/public-thg/2023-09-14-logo-e5b05e82d5de069a173f037a.jpg",
    isBestSeller: false
  },
  {
    countryCode: CountryCodeEnum.austria,
    vehicleType: ThgVehicleTypes.M1,
    amountInKwh: 1500,
    title: "report.thgVehicleClass.classes.at.M1.title",
    classSpeaking: "report.thgVehicleClass.classes.at.M1.classSpeaking",
    hint: "report.thgVehicleClass.classes.at.M1.hint",
    src: "https://static.mmmint.ai/public-thg/2023-09-14-logo-f3788982c40337cc4a923577.jpg",
    isBestSeller: true
  }
];

/**
 * Returns the kWh allowance for a given vehicle class and country, or
 * `undefined` if the combination is not configured.
 */
export function getKwhForVehicleClass(vehicleType: ThgVehicleTypes, countryCode: CountryCodeEnum): number | undefined {
  return VEHICLE_CLASS_CONFIG.find((v) => v.vehicleType === vehicleType && v.countryCode === countryCode)?.amountInKwh;
}
